/**
 * Fixtures do modo mock das telas de CRM.
 *
 * Cobre:
 *  - GET /pipeline                        → 3 pipelines (Comercial/Produção/Financeiro)
 *  - GET /pipeline-stage?pipeline_id=N    → board da pipeline pedida
 *  - GET /pipeline-stage/cards-list       → visão em lista
 *  - GET /crm-tag                         → etiquetas do CRM
 *
 * A pipeline Comercial (id 1) é a principal das capturas; Produção (id 2) é o
 * exemplo de uso operacional. As colunas são paginadas: `cards` traz a primeira
 * página e `cards_pagination.total` o total da etapa — mesmo comportamento da
 * API real.
 *
 * Valores monetários em CENTAVOS (o front divide por 100). Para uma nova
 * captura, edite os arrays de cards aqui.
 */

import moment from "moment";

const brl = reais => Math.round(reais * 100);

// ---------------------------------------------------------------------------
// Pessoas (responsáveis / membros)
// ---------------------------------------------------------------------------

const ADMINS = {
    marina: { id: 1, name: "Marina Souza", photo_url: null },
    carlos: { id: 2, name: "Carlos Mendes", photo_url: null },
    ana: { id: 3, name: "Ana Beatriz Costa", photo_url: null },
    rafael: { id: 4, name: "Rafael Almeida", photo_url: null },
    juliana: { id: 5, name: "Juliana Ribeiro", photo_url: null },
    pedro: { id: 6, name: "Pedro Henrique Lima", photo_url: null },
};

// ---------------------------------------------------------------------------
// Produtos usados nos cards de Produção (com a foto pública do fornecedor)
// ---------------------------------------------------------------------------

const PRODUCTS = {
    squeezeTritan: { name: "Squeeze em Tritan 800 mL",      img: "https://www.spotgifts.com.br/fotos/produtos/94620_set.jpg" },
    squeezeInox:   { name: "Squeeze Inox 550 mL",           img: "https://www.xbzbrindes.com.br/img/produtos/original/Squeeze-Inox-550ml-20486-1725457117.jpg" },
    canetaTouch:   { name: "Caneta Metal Touch",            img: "https://www.xbzbrindes.com.br/img/produtos/3/Caneta-Metal-Touch-VERDE-9903-1561639996.jpg" },
    canetaMetal:   { name: "Caneta Metal Esferográfica",    img: "https://www.xbzbrindes.com.br/img/produtos/original/Caneta-Metal-Esferografica-PRETO-17079-1693310068.jpg" },
    cadernoFlex:   { name: "Caderno A5 Capa Flexível",      img: "https://www.spotgifts.com.br/fotos/produtos/53626_set.jpg" },
    cadernoPalha:  { name: "Caderno A5 Capa Dura Palha",    img: "https://www.spotgifts.com.br/fotos/produtos/53632_set.jpg" },
    mochilaPoli:   { name: "Mochila Poliéster p/ Notebook", img: "https://www.xbzbrindes.com.br/img/produtos/3/Mochila-Poliester-para-Notebook-9049-1547496813.jpg" },
    mochilaNote:   { name: 'Mochila Notebook 15,6" 600D',   img: "https://www.spotgifts.com.br/fotos/produtos/92636_set.jpg" },
};

// ---------------------------------------------------------------------------
// Etiquetas
// ---------------------------------------------------------------------------

const TAGS = {
    urgente:  { id: 1, name: "Urgente", color: "#E31C58" },
    novo:     { id: 2, name: "Cliente novo", color: "#4C7DE0" },
    recompra: { id: 3, name: "Recompra", color: "#2FBF8F" },
    amostra:  { id: 4, name: "Amostra enviada", color: "#E0A33C" },
    alto:     { id: 5, name: "Alto valor", color: "#6C4CE0" },
    externa:  { id: 6, name: "Compra externa", color: "#3D5980" },
};

// ---------------------------------------------------------------------------
// Helpers de card
// ---------------------------------------------------------------------------

const today = moment().format("YYYY-MM-DD");
const inDays = n => moment().add(n, "days").format("YYYY-MM-DD");

let cardSeq = 1;

const makeCard = ({ code, company, city, state, customer, amount, admin, days, tags = [], task = null, product = null, qty = null }) => ({
    id: cardSeq++,
    code,
    amount: brl(amount),
    status: "open",
    color: "",
    days_in_stage: days,
    company: { trade_name: company, city, state },
    customer: { name: customer },
    admin,
    crm_tags: tags,
    next_pending_task: task,
    order_item: product ? {
        image_url: { small: product.img },
        product_name: product.name,
        subproduct_name: null,
        quantity: qty,
    } : null,
});

// Uma etapa do board: `cards` é a primeira página; `total`/`totalValue` são os
// números da etapa inteira (o que o rodapé da coluna mostra).
const stageBlock = ({ id, name, cards, total, totalValue }) => ({
    id,
    name,
    cards,
    cards_pagination: {
        total: total ?? cards.length,
        total_value: totalValue ?? cards.reduce((sum, c) => sum + c.amount, 0),
        current_page: 1,
        last_page: 1,
        per_page: 20,
    },
});

const boardTotals = stages => ({
    stages,
    total_cards: stages.reduce((s, st) => s + st.cards_pagination.total, 0),
    total_value: stages.reduce((s, st) => s + st.cards_pagination.total_value, 0),
});

// ---------------------------------------------------------------------------
// Pipeline COMERCIAL (id 1) — a principal
// ---------------------------------------------------------------------------

const COMERCIAL_STAGES = [
    stageBlock({
        id: 101, name: "Orçamento", total: 34, totalValue: brl(268400),
        cards: [
            makeCard({ code: "8841", company: "Construtora Horizonte", city: "São Paulo", state: "SP", customer: "João Prado", amount: 24450, admin: ADMINS.marina, days: 1, tags: [TAGS.urgente], task: { status: "pending", type: "call", date: today } }),
            makeCard({ code: "8839", company: "Farmácias VidaPlena", city: "Campinas", state: "SP", customer: "Beatriz Nunes", amount: 18700, admin: ADMINS.carlos, days: 2, tags: [TAGS.novo] }),
            makeCard({ code: "8836", company: "Colégio Novo Saber", city: "Curitiba", state: "PR", customer: "Marta Reis", amount: 15200, admin: ADMINS.ana, days: 1 }),
            makeCard({ code: "8834", company: "Tech Solutions", city: "Florianópolis", state: "SC", customer: "André Lopes", amount: 9480, admin: ADMINS.rafael, days: 4, task: { status: "pending", type: "email", date: inDays(-2) } }),
            makeCard({ code: "8831", company: "Rede Vitalis Odonto", city: "Belo Horizonte", state: "MG", customer: "Marcos Aguiar", amount: 12300, admin: ADMINS.juliana, days: 3, tags: [TAGS.novo] }),
        ],
    }),
    stageBlock({
        id: 102, name: "Follow up", total: 21, totalValue: brl(214900),
        cards: [
            makeCard({ code: "8814", company: "Banco Meridiano", city: "São Paulo", state: "SP", customer: "Sofia Cardoso", amount: 42900, admin: ADMINS.marina, days: 3, tags: [TAGS.alto], task: { status: "pending", type: "call", date: inDays(1) } }),
            makeCard({ code: "8809", company: "AgroTech Brasil", city: "Ribeirão Preto", state: "SP", customer: "Ricardo Melo", amount: 22375, admin: ADMINS.carlos, days: 5, tags: [TAGS.recompra] }),
            makeCard({ code: "8806", company: "Clínica Bem Estar", city: "Belo Horizonte", state: "MG", customer: "Paulo Vieira", amount: 11600, admin: ADMINS.pedro, days: 6 }),
            makeCard({ code: "8803", company: "Transportes Aliança", city: "Guarulhos", state: "SP", customer: "Débora Prado", amount: 16840, admin: ADMINS.ana, days: 2, task: { status: "pending", type: "meeting", date: inDays(2) } }),
        ],
    }),
    stageBlock({
        id: 103, name: "Amostra virtual", total: 12, totalValue: brl(168300),
        cards: [
            makeCard({ code: "8788", company: "Universidade Aurora", city: "Rio de Janeiro", state: "RJ", customer: "Tiago Barros", amount: 38360, admin: ADMINS.juliana, days: 4, tags: [TAGS.amostra] }),
            makeCard({ code: "8784", company: "Supermercados União", city: "Osasco", state: "SP", customer: "Camila Rocha", amount: 27400, admin: ADMINS.rafael, days: 2, tags: [TAGS.amostra] }),
            makeCard({ code: "8779", company: "Seguros Boa Praça", city: "Santos", state: "SP", customer: "Renata Lima", amount: 19900, admin: ADMINS.marina, days: 7 }),
        ],
    }),
    stageBlock({
        id: 104, name: "Contrato", total: 8, totalValue: brl(190700),
        cards: [
            makeCard({ code: "8752", company: "Logística Rota Certa", city: "Guarulhos", state: "SP", customer: "Helena Dias", amount: 64800, admin: ADMINS.carlos, days: 2, tags: [TAGS.alto] }),
            makeCard({ code: "8748", company: "Indústria Vetor", city: "Joinville", state: "SC", customer: "Fernando Alves", amount: 31500, admin: ADMINS.ana, days: 1, tags: [TAGS.recompra] }),
        ],
    }),
];

const COMERCIAL_BOARD = boardTotals(COMERCIAL_STAGES);

// ---------------------------------------------------------------------------
// Pipeline PRODUÇÃO (id 2) — exemplo operacional
// ---------------------------------------------------------------------------

const PRODUCAO_STAGES = [
    stageBlock({
        id: 201, name: "Pedido recebido", cards: [
            makeCard({ code: "10432", company: "Construtora Horizonte", city: "São Paulo", state: "SP", customer: "João Prado", product: PRODUCTS.squeezeTritan, qty: 500, amount: 12450, admin: ADMINS.marina, days: 1, tags: [TAGS.urgente] }),
            makeCard({ code: "10433", company: "Farmácias VidaPlena", city: "Campinas", state: "SP", customer: "Beatriz Nunes", product: PRODUCTS.canetaTouch, qty: 1000, amount: 8700, admin: ADMINS.carlos, days: 2 }),
            makeCard({ code: "10435", company: "Colégio Novo Saber", city: "Curitiba", state: "PR", customer: "Marta Reis", product: PRODUCTS.cadernoFlex, qty: 800, amount: 15200, admin: ADMINS.ana, days: 1, task: { status: "pending", type: "call", date: today } }),
        ],
    }),
    stageBlock({
        id: 202, name: "Compras", cards: [
            makeCard({ code: "10428", company: "AgroTech Brasil", city: "Ribeirão Preto", state: "SP", customer: "Ricardo Melo", product: PRODUCTS.mochilaPoli, qty: 150, amount: 22375, admin: ADMINS.rafael, days: 4 }),
            makeCard({ code: "10430", company: "Banco Meridiano", city: "São Paulo", state: "SP", customer: "Sofia Cardoso", product: PRODUCTS.squeezeInox, qty: 600, amount: 9900, admin: ADMINS.juliana, days: 3, tags: [TAGS.externa] }),
            makeCard({ code: "10425", company: "Clínica Bem Estar", city: "Belo Horizonte", state: "MG", customer: "Paulo Vieira", product: PRODUCTS.canetaMetal, qty: 1200, amount: 11600, admin: ADMINS.pedro, days: 5, task: { status: "pending", type: "email", date: inDays(-2) } }),
        ],
    }),
    stageBlock({
        id: 203, name: "Aguardando chegada", cards: [
            makeCard({ code: "10419", company: "Logística Rota Certa", city: "Guarulhos", state: "SP", customer: "Helena Dias", product: PRODUCTS.mochilaNote, qty: 120, amount: 27750, admin: ADMINS.marina, days: 8 }),
            makeCard({ code: "10421", company: "Universidade Aurora", city: "Rio de Janeiro", state: "RJ", customer: "Tiago Barros", product: PRODUCTS.cadernoPalha, qty: 400, amount: 18360, admin: ADMINS.carlos, days: 6 }),
        ],
    }),
    stageBlock({
        id: 204, name: "Personalização", cards: [
            makeCard({ code: "10410", company: "Supermercados União", city: "Osasco", state: "SP", customer: "Camila Rocha", product: PRODUCTS.squeezeTritan, qty: 700, amount: 14940, admin: ADMINS.ana, days: 3, task: { status: "pending", type: "task", date: inDays(1) } }),
            makeCard({ code: "10412", company: "Tech Solutions", city: "Florianópolis", state: "SC", customer: "André Lopes", product: PRODUCTS.canetaTouch, qty: 900, amount: 13050, admin: ADMINS.rafael, days: 2 }),
            makeCard({ code: "10415", company: "Construtora Horizonte", city: "São Paulo", state: "SP", customer: "João Prado", product: PRODUCTS.cadernoFlex, qty: 500, amount: 9500, admin: ADMINS.juliana, days: 4 }),
        ],
    }),
    stageBlock({
        id: 205, name: "Controle de qualidade", cards: [
            makeCard({ code: "10402", company: "Farmácias VidaPlena", city: "Campinas", state: "SP", customer: "Beatriz Nunes", product: PRODUCTS.mochilaPoli, qty: 200, amount: 17900, admin: ADMINS.pedro, days: 1 }),
            makeCard({ code: "10405", company: "Banco Meridiano", city: "São Paulo", state: "SP", customer: "Sofia Cardoso", product: PRODUCTS.squeezeInox, qty: 800, amount: 7920, admin: ADMINS.marina, days: 2 }),
        ],
    }),
    stageBlock({
        id: 206, name: "Separação", cards: [
            makeCard({ code: "10395", company: "Colégio Novo Saber", city: "Curitiba", state: "PR", customer: "Marta Reis", product: PRODUCTS.mochilaNote, qty: 100, amount: 18500, admin: ADMINS.carlos, days: 1 }),
            makeCard({ code: "10398", company: "AgroTech Brasil", city: "Ribeirão Preto", state: "SP", customer: "Ricardo Melo", product: PRODUCTS.cadernoPalha, qty: 300, amount: 13770, admin: ADMINS.ana, days: 2 }),
        ],
    }),
];

const PRODUCAO_BOARD = boardTotals(PRODUCAO_STAGES);

// ---------------------------------------------------------------------------
// Resolução do board por pipeline
// ---------------------------------------------------------------------------

const BOARDS = { 1: COMERCIAL_BOARD, 2: PRODUCAO_BOARD };

export const boardForPipeline = pipelineId => BOARDS[Number(pipelineId)] ?? COMERCIAL_BOARD;

// Visão em lista: achata as etapas do board pedido e injeta `stage_name`.
export const cardsListForPipeline = pipelineId => {
    const board = boardForPipeline(pipelineId);
    const cards = board.stages.flatMap(stage =>
        stage.cards.map(card => ({ ...card, stage_name: stage.name }))
    );

    return {
        cards,
        pagination: { current_page: 1, last_page: 1, per_page: 20, total: cards.length },
        total_cards: board.total_cards,
        total_value: board.total_value,
    };
};

// ---------------------------------------------------------------------------
// Lista de pipelines (GET /pipeline)
// ---------------------------------------------------------------------------

const membersOf = (...keys) => keys.map(k => ADMINS[k]);

// Etapas "leves" (só nome + open_cards) para os cards de pipeline em /crm.
const lightStages = board =>
    board.stages.map(stage => ({
        id: stage.id,
        name: stage.name,
        open_cards: stage.cards_pagination.total,
    }));

const COMERCIAL_PIPELINE = {
    id: 1,
    name: "Comercial",
    icon: "💼",
    stages: lightStages(COMERCIAL_BOARD),
    total_cards: 128,
    open_cards: COMERCIAL_BOARD.total_cards,
    open_value: COMERCIAL_BOARD.total_value,
    automations_count: 1,
    members: membersOf("marina", "carlos", "ana", "rafael", "juliana", "pedro"),
};

const PRODUCAO_PIPELINE = {
    id: 2,
    name: "Produção",
    icon: "🏭",
    stages: lightStages(PRODUCAO_BOARD),
    total_cards: PRODUCAO_BOARD.total_cards,
    open_cards: PRODUCAO_BOARD.total_cards,
    open_value: PRODUCAO_BOARD.total_value,
    automations_count: 1,
    members: membersOf("marina", "carlos", "ana", "rafael"),
};

const FINANCEIRO_PIPELINE = {
    id: 3,
    name: "Financeiro",
    icon: "💰",
    stages: [
        { id: 301, name: "Emissão de nota", open_cards: 14 },
        { id: 302, name: "Pagamento parcial", open_cards: 9 },
        { id: 303, name: "Pagamento confirmado", open_cards: 22 },
    ],
    total_cards: 96,
    open_cards: 45,
    open_value: brl(613720),
    automations_count: 1,
    members: membersOf("juliana", "pedro"),
};

export const PIPELINE_RESPONSE = {
    resources: [COMERCIAL_PIPELINE, PRODUCAO_PIPELINE, FINANCEIRO_PIPELINE],
};

export const CRM_TAG_RESPONSE = {
    resources: Object.values(TAGS),
};
